/**
 * Session Event Types - Custom events for session management.
 *
 * These events allow cross-component communication about session state
 * without tight coupling between providers.
 *
 * @see docs/architecture/decisions/0010-session-timeout-management.md
 */

/**
 * Custom event names for session management.
 */
export const SESSION_EVENTS = {
  /** Fired when IdP (Azure AD/Auth0) token refresh fails */
  IDP_SESSION_EXPIRED: "session:idp-expired",
  /** Fired when user activity is detected (existing) */
  ACTIVITY: "session:activity",
} as const;

/**
 * Authentication provider type.
 */
export type AuthProvider = "Azure" | "Auth0";

/**
 * Event detail for IdP session expiration.
 */
export interface IdpSessionExpiredEventDetail {
  /** The auth provider that reported the expiration */
  provider: AuthProvider;
  /** The error code from the IdP, if available */
  errorCode?: string;
  /** Human-readable error message */
  message?: string;
  /** Timestamp when the expiration was detected */
  timestamp: number;
}

/**
 * Type-safe event dispatcher for IdP session expiration.
 *
 * Dispatches a custom event that SessionTimeoutProvider listens for
 * to show the warning modal instead of immediately redirecting to login.
 *
 * @param detail - Event details including provider and error info
 *
 * @example
 * ```typescript
 * dispatchIdpSessionExpired({
 *   provider: 'Azure',
 *   errorCode: 'login_required',
 *   message: 'Token refresh failed',
 *   timestamp: Date.now(),
 * });
 * ```
 */
export function dispatchIdpSessionExpired(detail: IdpSessionExpiredEventDetail): void {
  window.dispatchEvent(new CustomEvent(SESSION_EVENTS.IDP_SESSION_EXPIRED, { detail }));
}
